// app/lib/productUtil.ts

import {authenticatedFetch} from './apiUtils';
import {
  AvailableDate,
  CategoryInfo,
  CategoryPill,
  CategorySummary,
  GroupEventDayAvailability,
  GroupEventDisplayProduct,
  GroupProductDisplay,
  GuideProfileData,
  IndividualProductDisplay,
  Product,
  ProductDisplay,
  ProductsResponse,
  UnifiedProductDisplay
} from '@/app/api/types';
import {formatInTimeZone} from "date-fns-tz";
import {getServerSession} from "next-auth/next";
import {authOptions} from "@/app/lib/auth";
import {extractIdFromSlug, extractIdSuffixFromSlug, generateGuideSlug} from './slugUtil';


const API_BASE_URL = process.env.NEXT_PUBLIC_API_URL;



/**
 * Type guard to check if a product is an individual product
 * @param product UnifiedProductDisplay to check
 * @returns boolean indicating if the product is an individual product
 */
export function isIndividualProduct(product: UnifiedProductDisplay): product is IndividualProductDisplay {
  return product.sessionType === 'INDIVIDUAL';
}

/**
 * Type guard to check if a product is a group product
 * @param product UnifiedProductDisplay to check
 * @returns boolean indicating if the product is a group product
 */
export function isGroupProduct(product: UnifiedProductDisplay): product is GroupProductDisplay {
  return product.sessionType === 'GROUP';
}

/**
 * Gets the price for display from a unified product
 * @param product UnifiedProductDisplay to get price from
 * @returns number representing the price
 */
export function getProductPrice(product: UnifiedProductDisplay): number {
  if (isIndividualProduct(product)) {
    return product.price;
  } else {
    // For group products, use the price of the first available schedule
    // or 0 if no schedules are available
    return product.schedules?.[0]?.pricePerSeat ?? 0;
  }
}


export async function getGuideProfileData(slugOrId: string): Promise<GuideProfileData> {
  try {
    // Extract the full ID from the slug or use the ID directly
    const guideId = /^[0-9a-fA-F]{24}$/.test(slugOrId) ? slugOrId : extractIdFromSlug(slugOrId);
    if (!guideId) {
      throw new Error('Ungültiger Guide-Link');
    }

    const [guideResponse, productsResponse] = await Promise.all([
      authenticatedFetch(`${API_BASE_URL}/ktor/guides/${guideId}`, {
        next: { revalidate: 2400 },
      }),
      authenticatedFetch(`${API_BASE_URL}/ktor/guides/product/${guideId}`, {
        next: { revalidate: 2400 },
      }),
    ]);

    if (!guideResponse.ok || !productsResponse.ok) {
      throw new Error('Guide nicht gefunden');
    }

    const [guide, productsData] = await Promise.all([
      guideResponse.json(),
      productsResponse.json() as Promise<UnifiedProductDisplay[]>,
    ]);

    // Generate the full name and slug for consistent URLs
    const fullName = `${guide.given_name} ${guide.family_name}`.trim();
    const slug = generateGuideSlug(fullName, guide.id);

    return {
      guide: {
        ...guide,
        fullName,
        slug,
      },
      products:
        productsData?.map(product => ({
          ...product,
          categories: Array.isArray(product.categories)
            ? product.categories.filter(cat => cat)
            : [],
          guide: {
            id: guide.id,
            givenName: guide.given_name,
            familyName: guide.family_name,
            profileImage: guide.picture || '',
            slug,
          },
        })) ?? [],
    };
  } catch (error) {
    console.error('Error fetching guide profile data:', error);
    throw error;
  }
}


/**
 * Helper function to get the full guide ID from a slug or ID
 * @param slugOrId The slug (name-1234) or ID
 * @returns The full guide ID
 * @throws Error if the slug format is invalid
 */
function getGuideIdFromSlug(slugOrId: string): string {
  // Check if this is already a full ID (matching MongoDB ObjectId format)
  if (/^[0-9a-fA-F]{24}$/.test(slugOrId)) {
    return slugOrId;
  }

  // Try to extract the ID suffix from the slug
  const idSuffix = extractIdSuffixFromSlug(slugOrId);
  if (!idSuffix) {
    throw new Error('Invalid slug format');
  }

  // Search through guides to find the full ID that ends with this suffix
  // For now, we'll have to fetch the guide data to get the full ID
  return slugOrId; // TODO: Implement guide lookup by slug suffix
}



/**
 * Checks if a specific date and time is blocked for group events
 * @param date Date to check
 * @param blockedDays List of blocked days and their slots
 * @returns boolean indicating if the date is blocked
 */
export function isDateBlocked(
  date: Date,
  blockedDays: GroupEventDayAvailability[]
): boolean {
  const dateStr = formatInTimeZone(date, 'Europe/Berlin', 'yyyy-MM-dd');
  const timeStr = formatInTimeZone(date, 'Europe/Berlin', 'HH:mm');

  const blockedDay = blockedDays.find(day =>
    formatInTimeZone(new Date(day.date), 'Europe/Berlin', 'yyyy-MM-dd') === dateStr
  );

  if (!blockedDay) return false;

  return blockedDay.blockedSlots.some(slot => {
    const slotStart = formatInTimeZone(new Date(slot.start), 'Europe/Berlin', 'HH:mm');
    const slotEnd = formatInTimeZone(new Date(slot.end), 'Europe/Berlin', 'HH:mm');
    return timeStr >= slotStart && timeStr <= slotEnd;
  });
}


/**
 * Fetches guide's calculated availability
 * @param guideId The ID of the guide
 * @returns Promise<{ availableDates: AvailableDate[], timezone: string }>
 */
export async function getGuideAvailability(guideId: string): Promise<{ availableDates: AvailableDate[], timezone: string }> {
  try {
    const response = await authenticatedFetch(
      `${API_BASE_URL}/ktor/guides/availability/calc/${guideId}`
    );

    if (!response.ok) {
      throw new Error('Failed to fetch guide availability');
    }

    const availabilityData = await response.json();
    console.log(JSON.stringify("availabilityData: " + availabilityData))
    return {
      availableDates: availabilityData.availableDates,
      timezone: availabilityData.availableDates[0]?.timezone || 'Europe/Berlin'
    };
  } catch (error) {
    console.error('Error fetching guide availability:', error);
    throw error;
  }
}


export function adaptProductDisplayToProduct(productDisplay: ProductDisplay): Product {
  return {
    id: productDisplay.id,
    name: productDisplay.name,
    description: productDisplay.description,
    price:  productDisplay.price.toString(),
    duration: productDisplay.duration,
    type: productDisplay.type,
    teaser_img: productDisplay.teaserImage,
    featured: productDisplay.featured,
    guideId: productDisplay.guide.id,
    category_id: productDisplay.categoryIds,
    sessionType: "INDIVIDUAL",
  };
}


// Modified getProductById function with logging and fix for productType

export async function getProductById(productId: string): Promise<ProductDisplay> {
  try {
    const productResponse = await authenticatedFetch(`${API_BASE_URL}/ktor/products/product/${productId}`, {
      next: { revalidate: 740 }
    });

    if (!productResponse.ok) {
      throw new Error('Failed to fetch product');
    }

    const rawProduct = await productResponse.json();

    // Transform the raw product to match our interface
    const transformedProduct: ProductDisplay = {
      id: rawProduct.id,
      name: rawProduct.name,
      description: rawProduct.description,
      price: rawProduct.price,
      duration: rawProduct.duration,
      // Check for both productType and type fields
      type: rawProduct.productType || rawProduct.type || 'CHAT',
      teaserImage: rawProduct.teaserImage || '/images/lp/cards/placeholder-image.jpg',
      featured: rawProduct.featured || false,
      sessionType: rawProduct.sessionType || 'INDIVIDUAL',
      categoryIds: rawProduct.categoryIds || [],
      categories: rawProduct.categories || [],
      guide: {
        id: rawProduct.guide.id,
        givenName: rawProduct.guide.givenName,
        familyName: rawProduct.guide.familyName,
        profileImage: rawProduct.guide.profileImage
      }
    };

    return transformedProduct;
  } catch (error) {
    console.error('Error fetching product:', error);
    throw error;
  }
}



export async function getGroupProducts(page: number = 1, limit: number = 6): Promise<{
  items: GroupEventDisplayProduct[];
  total: number;
  page: number;
  hasMore: boolean;
}> {
  try {
    const [productsResponse, categories] = await Promise.all([
      authenticatedFetch(
        `${API_BASE_URL}/ktor/products/group?page=${page - 1}&pageSize=${limit}`,
        { next: { revalidate: 300 } }
      ),
      getCategorySummary()
    ]);

    if (!productsResponse.ok) {
      throw new Error('Failed to fetch group products');
    }

    const products = await productsResponse.json();

    // Guard clause for undefined products
    if (!Array.isArray(products)) {
      console.error('Products is not an array:', products);
      throw new Error('Invalid response format: products is not an array');
    }

    // Create category map for lookup
    const categoryMap = new Map(categories.map(cat => [cat.id, cat]));

    const transformedItems: GroupEventDisplayProduct[] = products.map((item: any) => {
      return {
        ...item,
        guide: item.guide ?? {
          id: item.guideId,
          name: 'Unknown Guide',
          profileImage: null
        },
        categories: item.category_id
          ?.map((id: string) => {
            const category = categoryMap.get(id);
            return category ? { id, name: category.name } : null;
          })
          .filter(Boolean) || []
      };
    });

    const now = new Date();
    const validProducts = transformedItems.filter(product => {
      const hasFutureSchedules = product.schedules?.some(
        schedule => new Date(schedule.startTime) > now
      );
      if (!hasFutureSchedules) {
        console.log('Product filtered out due to no future schedules:', product.id); // Debug filtered products
      }
      return hasFutureSchedules;
    });

    return {
      items: validProducts,
      total: products.length,
      page,
      hasMore: products.length === limit
    };
  } catch (error) {
    console.error('Error fetching group products:', error);
    return {
      items: [],
      total: 0,
      page,
      hasMore: false
    };
  }
}

/**
 * Fetches a single group product by its ID
 * @param productId The ID of the group product to fetch
 * @returns Promise<GroupEventDisplayProduct>
 */
export async function getGroupProductById(productId: string, userId?: string): Promise<GroupEventDisplayProduct> {
  try {
    const [productResponse, categories] = await Promise.all([
      authenticatedFetch(`${API_BASE_URL}/ktor/products/group/${productId}${userId ? `?userId=${userId}` : ''}`, {
        next: { revalidate: 240 }
      }),
      getCategorySummary()
    ]);

    if (!productResponse.ok) {
      throw new Error('Failed to fetch group product');
    }

    const product = await productResponse.json();

    // Create category map for lookup
    const categoryMap = new Map(categories.map(cat => [cat.id, cat]));

    // Transform the product to match our interface
    const transformedProduct: GroupEventDisplayProduct = {
      ...product,
      guide: product.guide ?? {
        id: product.guideId,
        name: 'Unknown Guide',
        profileImage: null
      },
      categories: product.category_id
        ?.map((id: string) => {
          const category = categoryMap.get(id);
          return category?.name ? { id, name: category.name } : null;
        })
        .filter(Boolean) || []
    };

    return transformedProduct;
  } catch (error) {
    console.error('Error fetching group product:', error);
    throw error;
  }
}



/**
 * Transforms products by including category names from CategorySummary
 * @param products Products from the API
 * @param categories CategorySummary list
 * @returns Products with category information
 */
function enrichProductsWithCategories<T extends { categoryIds: string[]; categories: CategoryInfo[] }>(
  products: T[],
  categories: CategorySummary[]
): T[] {
  const categoryMap = new Map(categories.map(cat => [cat.id, cat.name]));

  return products.map(product => ({
    ...product,
    categories: product.categoryIds
      .map(id => {
        const name = categoryMap.get(id);
        return name ? {
          id,
          name,
          description: undefined,
          seoText: undefined,
          createdAt: undefined,
          updatedAt: undefined
        } : null;
      })
      .filter(Boolean)
  }))
}

/**
 * Fetches featured products with category information
 * @returns Promise<ProductDisplay[]>
 */
export async function getFeaturedProducts(): Promise<ProductDisplay[]> {
  try {
    const [productsResponse, categories] = await Promise.all([
      authenticatedFetch(`${API_BASE_URL}/ktor/products/featured`, {
        next: { revalidate: 1900 }
      }),
      getCategorySummary()
    ]);

    if (!productsResponse.ok) {
      console.error('Fehler beim Laden der empfohlenen Produkte:', productsResponse.statusText);
      return [];
    }

    const rawProducts = await productsResponse.json();
    if (!Array.isArray(rawProducts)) {
      console.error('Unerwartetes Datenformat für Produkte empfangen');
      return [];
    }

    // Transform the raw products to match our interface
    const transformedProducts = rawProducts
      .filter(product => product && typeof product === 'object')
      .map((product: any) => {
        // Properly handle guide information from the API response
        const guideData = product.guide || {};

        // Explicitly set sessionType to INDIVIDUAL for all featured products
        const sessionType = 'INDIVIDUAL';

        return {
          id: product.id,
          name: product.name || 'Unbenanntes Produkt',
          description: product.description || '',
          price: typeof product.price === 'number' ? product.price : 0,
          duration: product.duration || 0,
          // Map productType to type, fall back to 'CHAT' only if necessary
          type: product.productType || product.type || 'CHAT',
          teaserImage: product.teaserImage || '/images/lp/cards/placeholder-image.jpg',
          sessionType: product.sessionType || 'INDIVIDUAL', // Use the provided sessionType
          featured: true,
          categoryIds: Array.isArray(product.categoryIds) ? product.categoryIds : [],
          categories: [], // Will be populated by enrichProductsWithCategories
          guide: {
            id: guideData.id || '',
            givenName: guideData.givenName || guideData.given_name || '',
            familyName: guideData.familyName || guideData.family_name || '',
            profileImage: guideData.profileImage || guideData.picture || '/images/guide/placeholder.webp'
          }
        };
      })
      .filter(product => product.id);

    // Explicitly cast to ensure type safety
    const typedProducts = transformedProducts as ProductDisplay[];

    return enrichProductsWithCategories(typedProducts, categories);
  } catch (error) {
    console.error('Fehler beim Laden der empfohlenen Produkte:', error);
    return [];
  }
}

/**
 * Fetches the newest product from different guides to ensure diversity
 * @param categoryId Optional category filter
 * @param limit Maximum number of products to return
 * @returns Promise with products from different guides
 */
export async function getNewestProductsFromDifferentGuides(
  categoryId?: string,
  limit: number = 12
): Promise<ProductsResponse> {
  try {
    const queryParams = new URLSearchParams({
      limit: limit.toString(),
      ...(categoryId && { categoryId }),
    });

    const [productsResponse, categories] = await Promise.all([
      authenticatedFetch(
        `${API_BASE_URL}/ktor/products/newest-by-guides?${queryParams.toString()}`,
        { next: { revalidate: 1330 } }
      ),
      getCategorySummary()
    ]);

    if (!productsResponse.ok) {
      throw new Error('Failed to fetch diverse products');
    }

    // Parse the response data
    const responseData = await productsResponse.json();

    // Transform items to include formatted guide information
    const transformedItems = responseData.map((product: any) => {
      // Properly handle guide information from the API response
      const guideData = product.guide || {};

      return {
        ...product,
        sessionType: product.sessionType || 'INDIVIDUAL', // Explicitly set sessionType
        guide: {
          id: guideData.id || '',
          givenName: guideData.givenName || guideData.given_name || '',
          familyName: guideData.familyName || guideData.family_name || '',
          profileImage: guideData.profileImage || guideData.picture || ''
        }
      };
    });

    // Explicitly cast the transformed items to ensure type safety
    const typedItems = transformedItems as UnifiedProductDisplay[];

    return {
      items: enrichProductsWithCategories(typedItems, categories),
      total: typedItems.length,
      page: 1,
      hasMore: false
    };
  } catch (error) {
    console.error('Error fetching diverse products:', error);
    return {
      items: [],
      total: 0,
      page: 1,
      hasMore: false
    };
  }
}


/**
 * Fetches paginated products with category information
 */
export async function getProducts(
  categoryId?: string,
  page: number = 1,
  limit: number = 12,
  urgentOnly: boolean = false,
  needsCategoryEnrichment: boolean = true
): Promise<ProductsResponse> {
  try {
    const queryParams = new URLSearchParams({
      limit: limit.toString(),
      offset: ((page - 1) * limit).toString(),
      urgentOnly: urgentOnly.toString(),
      enrichCategories: needsCategoryEnrichment.toString(),
      ...(categoryId && { categoryId }),
    });

    const revalidateTimeInSeconds = urgentOnly ? 120 : 3600;

    const [productsResponse, categories2] = await Promise.all([
      authenticatedFetch(
        `${API_BASE_URL}/ktor/products?${queryParams.toString()}`,
        { next: { revalidate: revalidateTimeInSeconds  } }
      ),
      getCategorySummary()
    ]);

    if (!productsResponse.ok) {
      const errorBody = await productsResponse.text();
      console.error(`Failed to fetch products: ${productsResponse.status} ${productsResponse.statusText}`, errorBody);
      throw new Error(`Failed to fetch products (Status: ${productsResponse.status})`);
    }

    // Explicitly cast the response to the correct type
    const responseData = await productsResponse.json();
    const { items, total, hasMore } = responseData;

    // Transform items to include formatted guide information
    // and ensure sessionType is explicitly set
    const transformedItems = items.map((product: any) => {
      // Properly handle guide information from the API response
      const guideData = product.guide || {};

      // Set sessionType based on product type (CHAT/CALL are individual products)
      const sessionType = product.sessionType || 'INDIVIDUAL';

      return {
        ...product,
        sessionType, // Explicitly set sessionType
        guide: {
          id: guideData.id || '',
          givenName: guideData.givenName || guideData.given_name || '',
          familyName: guideData.familyName || guideData.family_name || '',
          profileImage: guideData.profileImage || guideData.picture || ''
        }
      };
    });

    // Explicitly cast the transformed items to ensure type safety
    const typedItems = transformedItems as UnifiedProductDisplay[];

    return {
      items: responseData.items as UnifiedProductDisplay[], // Daten kommen jetzt angereichert (oder nicht) von Ktor
      total: responseData.total,
      page,
      hasMore: responseData.hasMore
    };
  } catch (error) {
    console.error('Error fetching products:', error);
    return {
      items: [],
      total: 0,
      page,
      hasMore: false
    };
  }
}


/**
 * Fetches category summary including product counts and images.
 * Filters out categories that do not have a valid 'id'.
 * @returns Promise<CategorySummary[]> - Array of categories with valid IDs, or empty array on error.
 */
export async function getCategorySummary(): Promise<CategorySummary[]> {
  try {
    const isServer = typeof window === 'undefined';
    const baseUrl = isServer
      ? process.env.NEXT_PUBLIC_BASE_URL || 'http://localhost:3000'
      : '';
    const response = await fetch(`${baseUrl}/api/categories`, {
      next: { revalidate: 565 }
    });

    if (!response.ok) {
      // Log the status for more context
      console.error(`Error fetching category summary: ${response.status} ${response.statusText}`);
      throw new Error(`Failed to fetch category summary (Status: ${response.status})`);
    }

    // Get the raw data from the API
    const rawData: any[] = await response.json(); // Use any[] initially if unsure of API contract

    // --- Data Validation and Filtering ---
    if (!Array.isArray(rawData)) {
      console.error('Error fetching category summary: API did not return an array.');
      return []; // Return empty array if the response isn't an array
    }

    const validCategories = rawData.filter(category => {
      // Check if category exists and id is present, not null, and not an empty string after trimming
      const isValid = category &&
        typeof category.id !== 'undefined' &&
        category.id !== null &&
        String(category.id).trim() !== '';

      // Log invalid categories during development for debugging purposes
      if (!isValid && process.env.NODE_ENV === 'development') {
        console.warn('Filtered out category due to invalid/missing ID:', category);
      }

      return isValid;
    });
    // ------------------------------------

    // Explicitly cast to the expected type after filtering
    return validCategories as CategorySummary[];

  } catch (error) {
    // Catch errors from fetch itself or from response.json() or filtering
    console.error('Error in getCategorySummary function:', error);
    return []; // Return an empty array in case of any error
  }
}

/**
 * Fetches optimized category data for the category navigation bar
 * @returns Promise<CategoryPill[]>
 */
export async function getCategoryPills(): Promise<CategoryPill[]> {
  try {
    // Reuse category summary endpoint but transform the data
    const categories = await getCategorySummary();

    return categories.map(category => ({
      id: category.id,
      name: category.name,
      count: category.productCount
    }));
  } catch (error) {
    console.error('Error fetching category pills:', error);
    return [];
  }
}